import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { toast } from "sonner";

import { PageHeader } from "@/components/PageHeader";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  listLearningQueue,
  addLearningItem,
  updateLearningItem,
  deleteLearningItem,
  type LearningItem,
  type LearningStatus,
} from "@/server/learning-queue";
import { STAGE_LABELS, type StageKey } from "@/lib/stages";

export const Route = createFileRoute("/app/learning-queue")({
  beforeLoad: () => {
    throw redirect({ to: "/app/tools", search: { tab: "learning-queue" } });
  },
  component: () => null,
});

async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("You must be signed in.");
  return { authorization: `Bearer ${token}` };
}

const STATUS_LABEL: Record<LearningStatus, string> = {
  todo: "To learn",
  in_progress: "In progress",
  done: "Done",
};

const STATUS_COLOR: Record<LearningStatus, "default" | "primary" | "success"> = {
  todo: "default",
  in_progress: "primary",
  done: "success",
};

export function LearningQueuePanel({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const { user } = useAuth();
  const [items, setItems] = useState<LearningItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<LearningStatus>("todo");
  const [newSkill, setNewSkill] = useState("");
  const [newRationale, setNewRationale] = useState("");
  const [newPriority, setNewPriority] = useState(3);
  const [adding, setAdding] = useState(false);

  const refresh = async () => {
    if (!user) return;
    try {
      const headers = await getAuthHeaders();
      const res = await listLearningQueue({ headers });
      setItems(res.items);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const grouped = useMemo(() => {
    const m: Record<LearningStatus, LearningItem[]> = { todo: [], in_progress: [], done: [] };
    for (const it of items) m[it.status].push(it);
    return m;
  }, [items]);

  const stats = useMemo(
    () => ({
      todo: grouped.todo.length,
      in_progress: grouped.in_progress.length,
      done: grouped.done.length,
      total: items.length,
    }),
    [grouped, items],
  );

  const handleAdd = async () => {
    if (!newSkill.trim()) return;
    setAdding(true);
    try {
      const headers = await getAuthHeaders();
      const res = await addLearningItem({
        data: { skill: newSkill, rationale: newRationale || null, priority: newPriority },
        headers,
      });
      if (res.alreadyExists) {
        toast(`"${newSkill}" is already in your learning queue.`);
      } else {
        toast.success(`Added "${newSkill}" to your learning queue.`);
      }
      setNewSkill("");
      setNewRationale("");
      setNewPriority(3);
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not add.");
    } finally {
      setAdding(false);
    }
  };

  const handleStatusChange = async (id: string, status: LearningStatus) => {
    try {
      const headers = await getAuthHeaders();
      await updateLearningItem({ data: { id, status }, headers });
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Update failed.");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Remove this skill from your learning queue?")) return;
    try {
      const headers = await getAuthHeaders();
      await deleteLearningItem({ data: { id }, headers });
      setItems((s) => s.filter((it) => it.id !== id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Delete failed.");
    }
  };

  const visibleItems = grouped[tab];
  const completionPct = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;

  // Group visible items by source_stage so users see "Surfaced from {round} rejections"
  const sectioned = useMemo(() => {
    const buckets = new Map<string | null, LearningItem[]>();
    for (const it of visibleItems) {
      const key = it.source_stage ?? null;
      const list = buckets.get(key) ?? [];
      list.push(it);
      buckets.set(key, list);
    }
    // Sort: stage-tagged buckets first (in stage order), then "other"
    const stageOrder = Object.keys(STAGE_LABELS) as StageKey[];
    const sortedKeys: Array<string | null> = [
      ...stageOrder.filter((k) => buckets.has(k)),
      ...Array.from(buckets.keys()).filter(
        (k) => k !== null && !(stageOrder as string[]).includes(k as string),
      ),
    ];
    if (buckets.has(null)) sortedKeys.push(null);
    return sortedKeys.map((k) => ({
      key: k,
      label:
        k === null
          ? "Other"
          : STAGE_LABELS[k as StageKey]
            ? `Surfaced from ${STAGE_LABELS[k as StageKey]} rejections`
            : `Surfaced from ${k}`,
      items: buckets.get(k) ?? [],
    }));
  }, [visibleItems]);

  return (
    <Box>
      {!hideHeader && (
        <PageHeader
          title="Learning Queue"
          subtitle="Track skills you want to learn — added from rejection insights, JD gaps, or anywhere you spot a gap."
        />
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        {/* LEFT: list */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 2 }}>
                <SchoolOutlinedIcon color="primary" />
                <Typography variant="h6">Your queue</Typography>
                <Chip size="small" label={stats.total} />
              </Stack>

              {stats.total > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" sx={{ justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Overall progress
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.done} / {stats.total} done · {completionPct}%
                    </Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={completionPct} sx={{ height: 8, borderRadius: 4 }} />
                </Box>
              )}

              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v as LearningStatus)}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
              >
                <Tab value="todo" label={`To learn (${stats.todo})`} />
                <Tab value="in_progress" label={`In progress (${stats.in_progress})`} />
                <Tab value="done" label={`Done (${stats.done})`} />
              </Tabs>

              {loading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading…
                </Typography>
              ) : visibleItems.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography color="text.secondary" variant="body2">
                    Nothing in {STATUS_LABEL[tab].toLowerCase()} yet.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {sectioned.map((section) => (
                    <Box key={section.key ?? "__none"}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center", mb: 0.5 }}
                      >
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{ letterSpacing: 0.6 }}
                        >
                          {section.label}
                        </Typography>
                        <Chip size="small" label={section.items.length} variant="outlined" />
                      </Stack>
                      <Stack divider={<Divider />} spacing={0}>
                        {section.items.map((it) => (
                          <Stack
                            key={it.id}
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            sx={{ py: 1.5, alignItems: { sm: "center" } }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {it.skill}
                                </Typography>
                                <Chip size="small" label={`P${it.priority}`} variant="outlined" />
                                <Chip size="small" label={STATUS_LABEL[it.status]} color={STATUS_COLOR[it.status]} />
                                {it.source_stage && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    label={`from ${STAGE_LABELS[it.source_stage as StageKey] ?? it.source_stage}`}
                                  />
                                )}
                              </Stack>
                              {it.rationale && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                  {it.rationale}
                                </Typography>
                              )}
                            </Box>
                            <Stack direction="row" spacing={0.5}>
                              {it.resource_url && (
                                <Tooltip title="Open resource">
                                  <IconButton size="small" component="a" href={it.resource_url} target="_blank" rel="noreferrer">
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <TextField
                                select
                                size="small"
                                value={it.status}
                                onChange={(e) => handleStatusChange(it.id, e.target.value as LearningStatus)}
                                sx={{ minWidth: 140 }}
                              >
                                <MenuItem value="todo">To learn</MenuItem>
                                <MenuItem value="in_progress">In progress</MenuItem>
                                <MenuItem value="done">Done</MenuItem>
                              </TextField>
                              <IconButton size="small" onClick={() => handleDelete(it.id)} aria-label="Delete">
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* RIGHT: add form */}
        <Box sx={{ width: { md: 340 } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Add a skill
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Skill"
                  size="small"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  placeholder="e.g. Kubernetes, GraphQL federation"
                  fullWidth
                />
                <TextField
                  label="Why (optional)"
                  size="small"
                  value={newRationale}
                  onChange={(e) => setNewRationale(e.target.value)}
                  placeholder="Why this matters for your goal"
                  multiline
                  minRows={2}
                  fullWidth
                />
                <TextField
                  label="Priority"
                  size="small"
                  select
                  value={newPriority}
                  onChange={(e) => setNewPriority(Number(e.target.value))}
                  fullWidth
                >
                  <MenuItem value={1}>P1 — Urgent</MenuItem>
                  <MenuItem value={2}>P2 — High</MenuItem>
                  <MenuItem value={3}>P3 — Normal</MenuItem>
                  <MenuItem value={4}>P4 — Low</MenuItem>
                </TextField>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAdd}
                  disabled={!newSkill.trim() || adding}
                >
                  {adding ? "Adding…" : "Add to queue"}
                </Button>
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Tip: skills also appear here when you click <em>Add to learning queue</em> on a rejection insight in the Applications drawer.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Stack>
    </Box>
  );
}
